import React from "react";

import cx from "classnames";

import { setStateValue } from "@kazoohr/helpers";

import { Icon } from "../Icon";
import basePillStyles from "../Pill/Pill.scss";
import { Popover } from "../Popover";

import compoundPillStyles from "./CompoundPill.scss";

/**
 * Properties for the CompoundPill component
 */
export interface CompoundPillProps {
  /** Pill text */
  text: string;
  /** Component to be rendered inside the popover */
  popoverComponent: React.ReactNode;
  /** Class name applied to the pill */
  className?: string;
  /** Changes the style of the pill when true */
  lightBlueBackground?: boolean;
  /** Changes the style of the pill when true */
  solidBlueBackground?: boolean;
  /** Function called every time the popover opens */
  onPopoverOpen?: () => void;
  /** Prop to manage open/close state of the popover */
  isPopoverOpen?: boolean;
  /** Function to set the state of isPopoverOpen */
  setIsPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Class name applied to the popover */
  popoverClassName?: string;
  /** Indicates that the element can be focused, and its position in sequential keyboard navigation */
  tabIndex?: number;
  /** Renders as a data-testid prop on the Pill component */
  "data-testid"?: string;
  /** Closes when the user clicks away from the popover */
  closeOnClickAway?: boolean;
}

/**
 * A pill component that displays a popover when clicked
 */
export function CompoundPill({
  className,
  lightBlueBackground = false,
  solidBlueBackground = false,
  onPopoverOpen,
  isPopoverOpen = false,
  setIsPopoverOpen,
  popoverClassName,
  popoverComponent,
  tabIndex,
  "data-testid": testId,
  text,
  closeOnClickAway = true,
}: CompoundPillProps) {
  React.useEffect(() => {
    if (isPopoverOpen) {
      onPopoverOpen?.();
    }
  }, [isPopoverOpen, onPopoverOpen]);
  const toggleStateValue = setStateValue(setIsPopoverOpen, !isPopoverOpen);

  const pillStyles = cx(
    basePillStyles["pill"],
    basePillStyles["pill--gray"],
    basePillStyles["large"],
    compoundPillStyles["compound-pill"],
    className,
    {
      [compoundPillStyles["compound-pill--active"]]: isPopoverOpen,
      [compoundPillStyles["compound-pill--light-blue-bg"]]: lightBlueBackground,
      [compoundPillStyles["compound-pill--solid-blue-bg"]]: solidBlueBackground,
    }
  );

  const classNameForPopover = cx(
    compoundPillStyles["compound-pill--popover"],
    popoverClassName
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      toggleStateValue();
    }
  };

  const pillComponent = (
    <div
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      role="button"
      data-testid={testId}
      onClick={toggleStateValue}
      className={pillStyles}
    >
      <div data-testid="pill-text">{text}</div>
      <div className={compoundPillStyles.icon}>
        <Icon icon="caret_down" size={7} variant="thin" color="currentColor" />
      </div>
    </div>
  );

  return (
    <Popover
      isOpen={isPopoverOpen}
      onSetOpen={setIsPopoverOpen}
      popoverComponent={popoverComponent}
      parent={pillComponent}
      className={classNameForPopover}
      position="bottom-start"
      data-testid="meatBallMenu-popover"
      closeOnClickAway={closeOnClickAway}
    />
  );
}
